import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import {
  Receipt, CalendarDays, DollarSign, CreditCard, 
  Banknote, Building2, ShieldCheck, Clock, User,
  ChevronLeft, ChevronRight, Trash2, Eye
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('es-GT', {
    style: 'currency',
    currency: 'GTQ',
  }).format(amount || 0);
};

// Format date for display (dd/MM/yy)
const formatDateDisplay = (dateStr) => {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr + 'T12:00:00');
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    return `${day}/${month}/${year}`;
  } catch {
    return dateStr;
  }
};

const PAYMENT_ICONS = {
  'Efectivo': Banknote,
  'Tarjeta Crédito/Débito': CreditCard,
  'Transferencia': Building2,
  'Seguro Médico': ShieldCheck,
};

const Sales = () => {
  const { token, hasRole } = useAuth();
  const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Detail modal
  const [selectedSale, setSelectedSale] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  
  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [saleToDelete, setSaleToDelete] = useState(null);

  useEffect(() => {
    fetchDailySummary();
  }, [selectedDate]);

  const fetchDailySummary = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/sales/daily-summary`, {
        params: { fecha: selectedDate },
        headers: { Authorization: `Bearer ${token}` }
      });
      setSummary(response.data);
    } catch (error) {
      console.error('Error fetching sales:', error);
      toast.error('Error al cargar las ventas');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSale = async () => {
    if (!saleToDelete) return;
    
    try {
      await axios.delete(`${API_URL}/api/sales/${saleToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Venta eliminada');
      setDeleteDialogOpen(false);
      setSaleToDelete(null);
      fetchDailySummary();
    } catch (error) {
      console.error('Error deleting sale:', error);
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  const changeDate = (days) => {
    const current = new Date(selectedDate);
    current.setDate(current.getDate() + days);
    setSelectedDate(format(current, 'yyyy-MM-dd'));
  };

  const goToToday = () => {
    setSelectedDate(format(new Date(), 'yyyy-MM-dd'));
  };

  const formattedDate = format(new Date(selectedDate + 'T12:00:00'), "EEEE, d 'de' MMMM yyyy", { locale: es });
  const isToday = selectedDate === format(new Date(), 'yyyy-MM-dd');

  return (
    <div className="space-y-6" data-testid="sales-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl flex items-center justify-center shadow-lg">
            <Receipt className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="font-heading text-2xl font-bold text-slate-900">Ventas</h2>
            <p className="text-sm text-slate-500">Registro y cierre diario de ventas</p>
          </div>
        </div>
      </div>

      {/* Date Navigation */}
      <Card className="border-slate-200">
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => changeDate(-1)}
              className="text-slate-600"
            >
              <ChevronLeft className="w-5 h-5" />
            </Button>
            
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <CalendarDays className="w-5 h-5 text-emerald-600" />
                <span className="font-medium text-slate-800 capitalize">{formattedDate}</span>
                {isToday && (
                  <Badge className="bg-emerald-100 text-emerald-700 border-0">Hoy</Badge>
                )}
              </div>
              <Input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="w-40"
              />
              {!isToday && (
                <Button variant="outline" size="sm" onClick={goToToday}>
                  Ir a Hoy
                </Button>
              )}
            </div>
            
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => changeDate(1)}
              className="text-slate-600"
            >
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      {!loading && summary && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Total Sales */}
          <Card className="border-l-4 border-l-emerald-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total del Día</p>
                  <p className="text-2xl font-bold text-emerald-600">{formatCurrency(summary.total_monto)}</p>
                </div>
                <div className="w-12 h-12 bg-emerald-50 rounded-xl flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-emerald-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Number of Sales */}
          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Ventas Realizadas</p>
                  <p className="text-2xl font-bold text-blue-600">{summary.total_ventas}</p>
                </div>
                <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center">
                  <Receipt className="w-6 h-6 text-blue-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Payment Breakdown */}
          <Card className="border-l-4 border-l-purple-500">
            <CardContent className="p-4">
              <p className="text-sm text-slate-500 mb-2">Desglose por Pago</p>
              <div className="space-y-1">
                {Object.entries(summary.desglose_pago).length === 0 ? (
                  <p className="text-slate-400 text-sm">Sin ventas</p>
                ) : (
                  Object.entries(summary.desglose_pago).map(([method, amount]) => {
                    const Icon = PAYMENT_ICONS[method] || CreditCard;
                    return (
                      <div key={method} className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2">
                          <Icon className="w-4 h-4 text-slate-400" />
                          <span className="text-slate-600">{method}</span>
                        </div>
                        <span className="font-medium">{formatCurrency(amount)}</span>
                      </div>
                    );
                  })
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Sales List */}
      <Card className="border-slate-200">
        <CardHeader className="pb-4">
          <CardTitle className="text-lg flex items-center gap-2">
            <Clock className="w-5 h-5 text-emerald-600" />
            Ventas del Día
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-4 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin" />
            </div>
          ) : summary?.ventas?.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Receipt className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>No hay ventas registradas para este día</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Hora</TableHead>
                    <TableHead>Paciente</TableHead>
                    <TableHead>Exámenes</TableHead>
                    <TableHead>Forma de Pago</TableHead>
                    <TableHead>NIT</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead className="text-center">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {summary?.ventas?.map((sale) => {
                    const PayIcon = PAYMENT_ICONS[sale.forma_pago] || CreditCard;
                    const hora = sale.created_at ? format(new Date(sale.created_at), 'HH:mm') : '--:--';
                    
                    return (
                      <TableRow key={sale.id} className="hover:bg-slate-50">
                        <TableCell className="font-mono text-sm">{hora}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-slate-400" />
                            <span className="font-medium">{sale.nombre_paciente}</span>
                          </div>
                          {sale.medico_referente && (
                            <p className="text-xs text-slate-500 ml-6">Dr. {sale.medico_referente}</p>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="max-w-[200px]">
                            {sale.examenes?.map((ex, i) => (
                              <Badge key={i} variant="outline" className="mr-1 mb-1 text-xs">
                                {ex.tipo_examen}
                              </Badge>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <PayIcon className="w-4 h-4 text-slate-400" />
                            <span>{sale.forma_pago}</span>
                          </div>
                        </TableCell>
                        <TableCell className="font-mono text-sm">{sale.nit_facturacion || 'CF'}</TableCell>
                        <TableCell className="text-right font-semibold text-emerald-600">
                          {formatCurrency(sale.total)}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center justify-center gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => { setSelectedSale(sale); setIsDetailOpen(true); }}
                              className="text-slate-600 hover:text-slate-800"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            {hasRole(['SUPERADMIN', 'ADMIN']) && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => { setSaleToDelete(sale); setDeleteDialogOpen(true); }}
                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Cierre Diario Summary */}
      {!loading && summary && summary.total_ventas > 0 && (
        <Card className="border-2 border-emerald-200 bg-gradient-to-r from-emerald-50 to-teal-50">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2 text-emerald-700">
              <Receipt className="w-5 h-5" />
              Cierre del Día - {formattedDate}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                <p className="text-sm text-slate-500">Total Ventas</p>
                <p className="text-xl font-bold text-slate-800">{summary.total_ventas}</p>
              </div>
              
              {Object.entries(summary.desglose_pago).map(([method, amount]) => {
                const Icon = PAYMENT_ICONS[method] || CreditCard;
                return (
                  <div key={method} className="text-center p-4 bg-white rounded-lg shadow-sm">
                    <div className="flex items-center justify-center gap-1 text-sm text-slate-500">
                      <Icon className="w-4 h-4" />
                      {method}
                    </div>
                    <p className="text-xl font-bold text-slate-800">{formatCurrency(amount)}</p>
                  </div>
                );
              })}
              
              <div className="text-center p-4 bg-emerald-100 rounded-lg shadow-sm">
                <p className="text-sm text-emerald-600 font-medium">TOTAL DEL DÍA</p>
                <p className="text-2xl font-bold text-emerald-700">{formatCurrency(summary.total_monto)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Detail Modal */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="w-5 h-5 text-emerald-600" />
              Detalle de Venta
            </DialogTitle>
          </DialogHeader>
          {selectedSale && (
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-slate-500">Paciente:</span>
                  <span className="font-medium">{selectedSale.nombre_paciente}</span>
                </div>
                {selectedSale.medico_referente && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">Médico:</span>
                    <span>{selectedSale.medico_referente}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-slate-500">Fecha:</span>
                  <span>{formatDateDisplay(selectedSale.fecha)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">NIT:</span>
                  <span className="font-mono">{selectedSale.nit_facturacion || 'CF'}</span>
                </div>
              </div>
              
              <div className="border rounded-lg p-4 space-y-2">
                <p className="font-medium text-slate-700 mb-2">Exámenes:</p>
                {selectedSale.examenes?.map((ex, i) => (
                  <div key={i} className="flex justify-between text-sm">
                    <span>{ex.tipo_examen}</span>
                    <span className="font-medium">{formatCurrency(ex.precio)}</span>
                  </div>
                ))}
                <div className="pt-2 border-t flex justify-between">
                  <span>Subtotal:</span>
                  <span>{formatCurrency(selectedSale.subtotal)}</span>
                </div>
                {selectedSale.descuento > 0 && (
                  <div className="flex justify-between text-red-600">
                    <span>Descuento:</span>
                    <span>-{formatCurrency(selectedSale.descuento)}</span>
                  </div>
                )}
              </div>
              
              <div className="bg-emerald-50 rounded-lg p-4 flex justify-between items-center">
                <span className="font-medium text-emerald-700">Total:</span>
                <span className="text-xl font-bold text-emerald-700">{formatCurrency(selectedSale.total)}</span>
              </div>
              
              <div className="flex items-center gap-2 text-slate-500 text-sm">
                {(() => {
                  const Icon = PAYMENT_ICONS[selectedSale.forma_pago] || CreditCard;
                  return <Icon className="w-4 h-4" />;
                })()}
                <span>Pagado con {selectedSale.forma_pago}</span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar esta venta?</AlertDialogTitle>
            <AlertDialogDescription>
              {saleToDelete && (
                <>
                  Está a punto de eliminar la venta de "<strong>{saleToDelete.nombre_paciente}</strong>" 
                  por <strong>{formatCurrency(saleToDelete.total)}</strong>.
                  <br /><br />
                  Esta acción no se puede deshacer.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteSale}
              className="bg-red-500 hover:bg-red-600"
            >
              Sí, eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Sales;
